import { supabase } from '@/integrations/supabase/client';
import { safeLog } from '@/utils/safeLogger';

/**
 * Checks if the current user has admin role in the database
 * @returns true if user has admin role, false otherwise
 */
export async function checkDatabaseAdminRole(): Promise<boolean> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      safeLog.info('[checkDatabaseAdminRole] No authenticated user');
      return false;
    }
    
    const { data, error } = await supabase.rpc('has_role', {
      _user_id: user.id,
      _role: 'admin'
    });
    
    if (error) {
      safeLog.error('[checkDatabaseAdminRole] Error checking role', error);
      return false;
    }
    
    safeLog.info(`[checkDatabaseAdminRole] Role check result: ${data === true}`);
    return data === true;
  } catch (error) {
    safeLog.error('[checkDatabaseAdminRole] Exception', error as Error);
    return false;
  }
}

/**
 * Prüft ob wir im Add-In Kontext sind
 */
function isAddinContext(): boolean {
  const pathname = window.location.pathname;
  return window.location.search.includes('et=') || pathname.includes('/addin');
}

/**
 * Generiert einen stabilen Browser-Fingerprint basierend auf Hardware/Software-Eigenschaften
 */
async function generateBrowserFingerprint(): Promise<string> {
  const data = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    navigator.platform
  ].join('|');
  
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Hybrid-Ansatz: Browser Fingerprint + localStorage UUID für maximale Stabilität
 * Bei erstem Aufruf: Generiert Fingerprint + UUID und speichert in localStorage
 * Bei weiteren Aufrufen: Nutzt gespeicherten Device-ID
 */
export async function getDeviceId(): Promise<string> {
  const DEVICE_ID_KEY = 'device_id';
  
  // 1. Versuche Device-ID aus localStorage zu laden
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  
  if (!deviceId) {
    // 2. Generiere neuen Device-ID (Fingerprint + Random-Component)
    const fingerprint = await generateBrowserFingerprint();
    const randomPart = crypto.randomUUID().slice(0, 8);
    deviceId = `${fingerprint.slice(0, 16)}-${randomPart}`;
    
    // 3. Speichere für zukünftige Verwendung
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  
  return deviceId;
}

/**
 * Generiert device-spezifischen localStorage-Key für Admin-Auth-Session
 */
export async function getAdminAuthSessionKey(): Promise<string> {
  const deviceId = await getDeviceId();
  return `admin_auth_session_${deviceId}`;
}

/**
 * Generiert device-spezifischen localStorage-Key für Session-Timestamp
 */
export async function getAdminAuthTimestampKey(): Promise<string> {
  const deviceId = await getDeviceId();
  return `admin_auth_timestamp_${deviceId}`;
}

/**
 * Prüft ob eine gültige Admin-Auth-Session für dieses Gerät existiert
 * Add-In: sessionStorage (Session endet bei Browser-Neustart)
 * Web: localStorage (30 Tage gültig)
 * @returns true wenn Session gültig, false wenn abgelaufen oder nicht vorhanden
 */
export async function checkAdminAuthSession(): Promise<boolean> {
  const SESSION_VALIDITY_DAYS = 30;
  const sessionKey = await getAdminAuthSessionKey();
  const timestampKey = await getAdminAuthTimestampKey();
  
  const storage = isAddinContext() ? sessionStorage : localStorage;
  
  const session = storage.getItem(sessionKey);
  const timestamp = storage.getItem(timestampKey);
  
  if (!session || !timestamp) {
    return false;
  }
  
  const age = Date.now() - parseInt(timestamp);
  const validityMs = SESSION_VALIDITY_DAYS * 24 * 60 * 60 * 1000;
  
  if (age >= validityMs) {
    // Session abgelaufen → Cleanup
    storage.removeItem(sessionKey);
    storage.removeItem(timestampKey);
    return false;
  }
  
  return true;
}

/**
 * Speichert eine neue Admin-Auth-Session für dieses Gerät
 * Add-In: sessionStorage (nur aktuelle Browser-Session)
 * Web: localStorage (30 Tage gültig)
 */
export async function saveAdminAuthSession(): Promise<void> {
  const sessionKey = await getAdminAuthSessionKey();
  const timestampKey = await getAdminAuthTimestampKey();
  
  const storage = isAddinContext() ? sessionStorage : localStorage;
  
  storage.setItem(sessionKey, 'true');
  storage.setItem(timestampKey, Date.now().toString());
}

/**
 * Löscht die Admin-Auth-Session für dieses Gerät
 * Add-In: sessionStorage
 * Web: localStorage
 */
export async function clearAdminAuthSession(): Promise<void> {
  const sessionKey = await getAdminAuthSessionKey();
  const timestampKey = await getAdminAuthTimestampKey();
  
  const storage = isAddinContext() ? sessionStorage : localStorage;
  
  storage.removeItem(sessionKey);
  storage.removeItem(timestampKey);
}
